/* ═══════════════════════════════════════
   STEADFAST SELECTIONS — Theme JS
   ═══════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {

  /* ─── Scroll-triggered fade-up ─── */
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('.fade-up').forEach(el => observer.observe(el));

  /* ─── Sticky nav ─── */
  // Nav background is always on now (is-scrolled is baked into the markup in header.liquid).
  // Previous scroll-based toggle removed — the transparent-over-hero treatment was too
  // visually noisy with the logo image on top of the communion photography.

  /* ─── Mobile nav toggle ─── */
  const navToggle = document.querySelector('[data-site-nav-toggle]');
  const navMenu = document.querySelector('[data-site-nav-menu]');
  const searchToggle = document.querySelector('[data-search-toggle]');
  const searchPanel = document.querySelector('[data-search-panel]');

  function closeNavDrawer() {
    if (!navMenu || !navToggle) return;
    navMenu.classList.remove('is-open');
    navToggle.classList.remove('is-active');
    navToggle.setAttribute('aria-expanded', 'false');
  }
  function closeSearchPanel() {
    if (!searchPanel || !searchToggle) return;
    searchPanel.classList.remove('is-open');
    searchToggle.setAttribute('aria-expanded', 'false');
  }

  if (navToggle && navMenu) {
    navToggle.addEventListener('click', () => {
      closeSearchPanel();
      const isOpen = navMenu.classList.toggle('is-open');
      navToggle.classList.toggle('is-active', isOpen);
      navToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
    // Close drawer when a link is tapped
    navMenu.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => closeNavDrawer());
    });
  }

  /* ─── Predictive search ─── */
  if (searchToggle && searchPanel) {
    const searchInput = searchPanel.querySelector('[data-search-input]');
    const searchResults = searchPanel.querySelector('[data-search-results]');
    const searchClose = searchPanel.querySelector('[data-search-close]');
    let debounceTimer = null;
    let inFlight = null;

    function setHint(msg) { searchResults.innerHTML = '<p class="site-search-panel__hint">' + msg + '</p>'; }
    function escapeHTML(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

    function pickImg(v) {
      if (!v) return '';
      if (typeof v === 'string') return v;
      return v.url || v.src || '';
    }
    function formatPrice(v) {
      if (v == null || v === '') return '';
      const s = String(v);
      if (s.indexOf('$') !== -1) return s; // already formatted
      const n = parseFloat(s);
      if (isNaN(n)) return '';
      return '$' + n.toFixed(2);
    }

    function renderResults(products, query) {
      if (!products.length) {
        setHint('No products match "' + escapeHTML(query) + '".');
        return;
      }
      const items = products.map(p => {
        let imgUrl = pickImg(p.featured_image) || pickImg(p.image);
        if (imgUrl && imgUrl.indexOf('//') === 0) imgUrl = 'https:' + imgUrl;
        const priceVal = p.price != null ? p.price : p.price_min;
        const price = formatPrice(priceVal);
        return (
          '<a class="site-search-result" href="' + p.url + '">' +
            (imgUrl ? '<img class="site-search-result__img" src="' + escapeHTML(imgUrl) + '" alt="" loading="lazy">' : '<span class="site-search-result__img"></span>') +
            '<span class="site-search-result__body">' +
              '<span class="site-search-result__title">' + escapeHTML(p.title) + '</span>' +
              (price ? '<span class="site-search-result__price">' + escapeHTML(price) + '</span>' : '') +
            '</span>' +
          '</a>'
        );
      }).join('');
      const viewAll = '<a class="site-search-panel__view-all" href="/search?q=' + encodeURIComponent(query) + '&type=product">View all results →</a>';
      searchResults.innerHTML = items + viewAll;
    }

    async function runSearch(q) {
      const query = (q || '').trim();
      if (query.length < 2) { setHint('Start typing to see products.'); return; }
      setHint('Searching…');
      if (inFlight) inFlight.abort();
      inFlight = new AbortController();
      try {
        const url = '/search/suggest.json?q=' + encodeURIComponent(query)
          + '&resources[type]=product'
          + '&resources[limit]=8'
          + '&resources[options][unavailable_products]=last'
          + '&resources[options][fields]=title,vendor,product_type,tag';
        const resp = await fetch(url, { signal: inFlight.signal, headers: { 'Accept': 'application/json' } });
        const data = await resp.json();
        const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
        renderResults(products, query);
      } catch (e) {
        if (e.name === 'AbortError') return;
        console.error('Predictive search failed:', e);
        setHint('Search is unavailable right now.');
      }
    }

    function openSearch() {
      closeNavDrawer();
      searchPanel.classList.add('is-open');
      searchToggle.setAttribute('aria-expanded', 'true');
      setTimeout(() => searchInput && searchInput.focus(), 80);
    }

    searchToggle.addEventListener('click', () => {
      if (searchPanel.classList.contains('is-open')) closeSearchPanel();
      else openSearch();
    });
    if (searchClose) searchClose.addEventListener('click', closeSearchPanel);

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => runSearch(searchInput.value), 180);
      });
    }

    // Esc closes
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchPanel.classList.contains('is-open')) closeSearchPanel();
    });

    // Click outside closes
    document.addEventListener('click', (e) => {
      if (!searchPanel.classList.contains('is-open')) return;
      if (searchPanel.contains(e.target) || searchToggle.contains(e.target)) return;
      closeSearchPanel();
    });
  }

  /* ─── Hero slideshow ─── */
  const heroSlideshow = document.querySelector('[data-hero-slideshow].hero--slideshow');
  if (heroSlideshow) initHeroSlideshow(heroSlideshow);

  /* ─── Communion Carousel ─── */
  const carousel = document.querySelector('[data-communion-carousel]');
  if (carousel) {
    initCommunionCarousel(carousel);
  }

  /* ─── Communion Set Builder ─── */
  const builder = document.querySelector('[data-communion-builder]');
  if (builder) {
    initCommunionBuilder(builder);
  }

  /* ─── Product Form (variant selection, price, image, submit state) ─── */
  const productForm = document.querySelector('.product-form');
  if (productForm) {
    initProductForm(productForm);
  }

  /* ─── Cart (AJAX line updates) ─── */
  const cartForm = document.querySelector('[data-cart-form]');
  if (cartForm) {
    initCartContent(cartForm);
  }
});

/* Shared helper — format cents (Shopify cart/product JSON) into a USD string */
function formatMoney(cents) {
  if (typeof cents !== 'number') return '';
  return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function initHeroSlideshow(slideshow) {
  const slides = slideshow.querySelectorAll('.hero-slide');
  if (slides.length < 2) return;

  const dots = slideshow.querySelectorAll('[data-slide-dot]');
  const prevBtn = slideshow.querySelector('[data-slide-prev]');
  const nextBtn = slideshow.querySelector('[data-slide-next]');
  const speed = (parseFloat(slideshow.dataset.rotationSpeed) || 0) * 1000;
  let currentIndex = 0;
  let autoTimer = null;

  function goTo(index) {
    currentIndex = (index + slides.length) % slides.length;
    slides.forEach((s, i) => s.classList.toggle('is-active', i === currentIndex));
    dots.forEach((d, i) => d.classList.toggle('is-active', i === currentIndex));
    resetAutoTimer();
  }

  function resetAutoTimer() {
    if (autoTimer) clearInterval(autoTimer);
    if (speed > 0) autoTimer = setInterval(() => goTo(currentIndex + 1), speed);
  }

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(currentIndex - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(currentIndex + 1));
  dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

  // Pause auto-rotate on hover (desktop) / touch (mobile)
  slideshow.addEventListener('mouseenter', () => { if (autoTimer) { clearInterval(autoTimer); autoTimer = null; } });
  slideshow.addEventListener('mouseleave', resetAutoTimer);

  resetAutoTimer();
}

function initCommunionCarousel(carousel) {
  const prevBtn = carousel.querySelector('[data-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-carousel-next]');
  const dotsContainer = carousel.querySelector('[data-carousel-dots]');
  let autoPlayTimer = null;
  const speed = parseInt(carousel.dataset.carouselSpeed || '5') * 1000;

  function getActiveTrack() {
    return carousel.querySelector('.communion-carousel__track[style=""], .communion-carousel__track:not([style*="display:none"]), .communion-carousel__track:not([style*="display: none"])') ||
           carousel.querySelector('.communion-carousel__track');
  }

  function getSlides(track) {
    return track ? Array.from(track.querySelectorAll('.communion-carousel__slide')) : [];
  }

  function showSlide(index) {
    const track = getActiveTrack();
    const slides = getSlides(track);
    if (slides.length === 0) return;

    // Pause any video on the slide we're leaving
    slides.forEach(s => {
      const vid = s.querySelector('video');
      if (vid && !vid.paused) vid.pause();
    });

    slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
    buildDots(slides.length, index);
  }

  function currentIndex() {
    const track = getActiveTrack();
    const slides = getSlides(track);
    return slides.findIndex(s => s.classList.contains('is-active'));
  }

  function navigate(dir) {
    const track = getActiveTrack();
    const slides = getSlides(track);
    if (slides.length === 0) return;
    const cur = currentIndex();
    const next = (cur + dir + slides.length) % slides.length;
    showSlide(next);
    resetAutoPlay();
  }

  function buildDots(count, activeIndex) {
    dotsContainer.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.className = 'communion-carousel__dot' + (i === activeIndex ? ' is-active' : '');
      dot.setAttribute('aria-label', `Slide ${i + 1}`);
      dot.addEventListener('click', () => { showSlide(i); resetAutoPlay(); });
      dotsContainer.appendChild(dot);
    }
  }

  function resetAutoPlay() {
    if (autoPlayTimer) clearInterval(autoPlayTimer);
    // Don't auto-advance away from a video slide — user controls playback
    const track = getActiveTrack();
    const slides = getSlides(track);
    const cur = currentIndex();
    const currentSlide = slides[cur];
    if (currentSlide && currentSlide.dataset.slideType === 'video') return;
    if (speed > 0) {
      autoPlayTimer = setInterval(() => navigate(1), speed);
    }
  }

  prevBtn.addEventListener('click', () => navigate(-1));
  nextBtn.addEventListener('click', () => navigate(1));

  // Expose switchFinish for the builder's finish toggle
  carousel._switchFinish = function(finish) {
    carousel.querySelectorAll('.communion-carousel__track').forEach(track => {
      // Pause any playing video on the track we're leaving
      track.querySelectorAll('video').forEach(v => { if (!v.paused) v.pause(); });
      track.style.display = track.dataset.carouselFinish === finish ? '' : 'none';
    });
    // Reset to first slide of the new track
    const newTrack = carousel.querySelector(`.communion-carousel__track[data-carousel-finish="${finish}"]`);
    if (newTrack) {
      const slides = Array.from(newTrack.querySelectorAll('.communion-carousel__slide'));
      slides.forEach((s, i) => s.classList.toggle('is-active', i === 0));
      buildDots(slides.length, 0);
    }
    resetAutoPlay();
  };

  // Initial dots
  const track = getActiveTrack();
  const slides = getSlides(track);
  if (slides.length > 0) buildDots(slides.length, 0);
  resetAutoPlay();
}

function initCommunionBuilder(builder) {
  const state = {};
  const rows = builder.querySelectorAll('[data-product-id]');
  const sidebar = builder.querySelector('[data-builder-sidebar]');
  const summaryList = sidebar.querySelector('[data-summary-list]');
  const subtotalEl = sidebar.querySelector('[data-subtotal]');
  const itemCountEl = sidebar.querySelector('[data-item-count]');
  const addBtn = sidebar.querySelector('[data-add-to-cart-btn]');
  const finishBtns = builder.querySelectorAll('[data-finish-btn]');

  // Visualizer tuning — desktop default + mobile/tablet scaled-down sets.
  // The mobile set is selected when the visualizer canvas is shorter (mobile breakpoint).
  const VIZ_TUNE_DESKTOP = { ctw:280, cto:34, ctlw:280, ctly:36, btw:200, bto:36, btlw:170, btly:32, bridge:0, offw:140, offo:18 };
  const VIZ_TUNE_TABLET  = { ctw:170, cto:20, ctlw:170, ctly:22, btw:122, bto:22, btlw:104, btly:19, bridge:0, offw:86,  offo:12 };
  const VIZ_TUNE_PHONE   = { ctw:140, cto:16, ctlw:140, ctly:18, btw:100, bto:18, btlw:86,  btly:16, bridge:0, offw:72,  offo:10 };
  function pickVizTune() {
    if (!vizStackEl) return VIZ_TUNE_DESKTOP;
    const w = window.innerWidth || document.documentElement.clientWidth;
    if (w <= 640) return VIZ_TUNE_PHONE;
    if (w <= 1024) return VIZ_TUNE_TABLET;
    return VIZ_TUNE_DESKTOP;
  }
  let VIZ_TUNE = VIZ_TUNE_DESKTOP;
  let currentFinish = 'gold';
  let viewMode = 'auto';
  let vizAssets = {};
  const vizAssetsScript = sidebar.querySelector('[data-visualizer-assets]');
  if (vizAssetsScript) {
    try { vizAssets = JSON.parse(vizAssetsScript.textContent); } catch (e) { console.error('Visualizer assets parse failed', e); }
  }
  const vizStackEl = sidebar.querySelector('[data-visualizer-stack]');
  const vizBadgeEl = sidebar.querySelector('[data-visualizer-badge]');

  function prepopulate(finish) {
    builder.querySelectorAll('[data-finish-group="' + finish + '"] [data-product-id]').forEach(row => {
      const id = row.dataset.productId;
      const name = (row.dataset.name || '').toLowerCase();
      if (name === 'cup tray' || name === 'cup tray lid' || name === 'bread tray' || name.indexOf('offering plate') === 0) {
        state[id] = 1;
        const d = row.querySelector('[data-qty-display]');
        if (d) d.textContent = '1';
      }
    });
  }

  // Initialize quantities to 0
  rows.forEach(row => {
    const id = row.dataset.productId;
    state[id] = 0;
  });
  // Prepopulate one of each main piece on the active (gold) finish
  prepopulate(currentFinish);

  function updateUI() {
    let totalItems = 0;
    let totalPrice = 0;
    let summaryHTML = '';

    rows.forEach(row => {
      const id = row.dataset.productId;
      const qty = state[id] || 0;
      const price = parseFloat(row.dataset.price);
      const name = row.dataset.name;
      const color = row.dataset.color || 'var(--color-accent)';
      const qtyDisplay = row.querySelector('[data-qty-display]');

      if (qtyDisplay) qtyDisplay.textContent = qty;

      // Update row active state
      if (qty > 0) {
        row.classList.add('is-active');
        totalItems += qty;
        totalPrice += qty * price;
        summaryHTML += `
          <div class="builder__sidebar-item">
            <div style="display:flex;align-items:center;gap:10px">
              <div class="builder__sidebar-dot" style="background:${color}"></div>
              <span>${name}</span>
              <span style="color:var(--color-text-faint)">× ${qty}</span>
            </div>
            <span style="font-weight:600">$${(qty * price).toFixed(2)}</span>
          </div>`;
      } else {
        row.classList.remove('is-active');
      }

      // Update line total display
      const lineTotal = row.querySelector('[data-line-total]');
      if (lineTotal) {
        lineTotal.textContent = qty > 0 ? `× ${qty} = $${(qty * price).toFixed(2)}` : '';
        lineTotal.style.display = qty > 0 ? 'inline' : 'none';
      }
    });

    // Update sidebar summary list (visualizer always visible, summary list shows line items)
    summaryList.innerHTML = summaryHTML;

    subtotalEl.textContent = `$${totalPrice.toFixed(2)}`;
    itemCountEl.textContent = `${totalItems} item${totalItems !== 1 ? 's' : ''}`;

    // Render visualizer
    renderVisualizer();

    // Update button
    addBtn.disabled = totalItems === 0;
    addBtn.textContent = totalItems > 0 ? `Add ${totalItems} Items to Cart` : 'Add Set to Cart';
    if (totalItems > 0) {
      addBtn.style.background = 'var(--color-accent)';
      addBtn.style.color = 'var(--color-bg)';
      addBtn.style.cursor = 'pointer';
    } else {
      addBtn.style.background = 'rgba(245,240,232,.06)';
      addBtn.style.color = 'var(--color-text-faint)';
      addBtn.style.cursor = 'default';
    }
  }

  // Quantity buttons
  rows.forEach(row => {
    const id = row.dataset.productId;
    const minusBtn = row.querySelector('[data-qty-minus]');
    const plusBtn = row.querySelector('[data-qty-plus]');

    if (minusBtn) minusBtn.addEventListener('click', () => { state[id] = Math.max(0, (state[id] || 0) - 1); updateUI(); });
    if (plusBtn) plusBtn.addEventListener('click', () => { state[id] = (state[id] || 0) + 1; updateUI(); });
  });

  // Finish toggle
  finishBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const finish = btn.dataset.finish;
      // Toggle visibility of product groups
      builder.querySelectorAll('[data-finish-group]').forEach(group => {
        group.style.display = group.dataset.finishGroup === finish ? 'block' : 'none';
      });
      finishBtns.forEach(b => {
        b.classList.remove('is-active', 'is-active-silver');
        if (b.dataset.finish === finish) {
          b.classList.add(finish === 'silver' ? 'is-active-silver' : 'is-active');
        }
      });
      // Update sidebar finish label
      const finishLabel = sidebar.querySelector('[data-finish-label]');
      if (finishLabel) finishLabel.textContent = finish.charAt(0).toUpperCase() + finish.slice(1);
      // Switch carousel to match finish (carousel handles its own internal video pausing now)
      const carousel = document.querySelector('[data-communion-carousel]');
      if (carousel && carousel._switchFinish) carousel._switchFinish(finish);
      // Reset all quantities, then re-prepopulate one of each main piece on the new finish
      currentFinish = finish;
      Object.keys(state).forEach(k => state[k] = 0);
      rows.forEach(r => { const d = r.querySelector('[data-qty-display]'); if (d) d.textContent = '0'; });
      prepopulate(currentFinish);
      updateUI();
    });
  });

  // View toggle (Auto / Combined / Separate)
  const vizToggleBtns = sidebar.querySelectorAll('[data-visualizer-toggle] button');
  vizToggleBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      viewMode = btn.dataset.view;
      vizToggleBtns.forEach(b => b.classList.toggle('is-active', b.dataset.view === viewMode));
      renderVisualizer();
    });
  });

  // Add to cart
  addBtn.addEventListener('click', async () => {
    if (addBtn.disabled) return;
    const items = [];
    rows.forEach(row => {
      const id = row.dataset.productId;
      const variantId = row.dataset.variantId;
      const qty = state[id] || 0;
      if (qty > 0 && variantId) {
        items.push({ id: parseInt(variantId), quantity: qty });
      }
    });

    if (items.length === 0) return;

    addBtn.textContent = 'Adding...';
    addBtn.disabled = true;

    try {
      const response = await fetch(window.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (response.ok) {
        addBtn.textContent = '✓ Added to Cart!';
        addBtn.style.background = 'var(--color-green)';
        setTimeout(() => {
          addBtn.style.background = 'var(--color-accent)';
          updateUI();
        }, 2500);
      } else {
        addBtn.textContent = 'Error — try again';
        setTimeout(() => updateUI(), 2000);
      }
    } catch (e) {
      addBtn.textContent = 'Error — try again';
      setTimeout(() => updateUI(), 2000);
    }
  });

  // ─── Visualizer rendering ───
  function getVisualizerCounts() {
    const counts = { cupTray: 0, cupTrayLid: 0, breadTray: 0, breadTrayLid: 0, offeringTray: 0 };
    let interior = 'blue';
    builder.querySelectorAll('[data-finish-group="' + currentFinish + '"] [data-product-id]').forEach(row => {
      const id = row.dataset.productId;
      const qty = state[id] || 0;
      if (qty === 0) return;
      const name = (row.dataset.name || '').toLowerCase();
      if (name === 'cup tray') counts.cupTray += qty;
      else if (name === 'cup tray lid') counts.cupTrayLid += qty;
      else if (name === 'bread tray') counts.breadTray += qty;
      else if (name === 'bread tray lid') counts.breadTrayLid += qty;
      else if (name.indexOf('offering plate') === 0) {
        counts.offeringTray += qty;
        if (name.indexOf('red') !== -1) interior = 'red';
        else if (name.indexOf('blue') !== -1) interior = 'blue';
      }
    });
    return { counts, interior };
  }

  function shouldCombineView(counts) {
    if (viewMode === 'combined') return true;
    if (viewMode === 'separate') return false;
    return counts.cupTray > 0 && counts.breadTray > 0 && counts.cupTrayLid > 0 && counts.breadTrayLid === 0;
  }

  function vizImg(piece, finish, interior) {
    if (piece === 'cupTray')      return vizAssets[finish + '_cup_tray'] || '';
    if (piece === 'breadTray')    return vizAssets[finish + '_bread_tray'] || '';
    if (piece === 'breadTrayLid') return vizAssets[finish + '_lid'] || '';
    if (piece === 'cupTrayLid')   return vizAssets[finish + '_lid'] || '';
    if (piece === 'offeringTray') return vizAssets[finish + '_offering_' + interior] || '';
    return '';
  }

  function vizPieceHTML(src, width, bottom, opacity) {
    if (!src) return '';
    return '<img class="visualizer__piece" src="' + src + '" style="width:' + width + 'px;bottom:' + bottom + 'px;opacity:' + (opacity || 1) + ';" alt="">';
  }

  function vizOfferingStack(c, f, interior) {
    if (c.offeringTray === 0) return '';
    const t = VIZ_TUNE;
    const src = vizImg('offeringTray', f, interior);
    if (!src) return '';
    let parts = '';
    let bottom = 22;
    for (let n = 0; n < c.offeringTray; n++) {
      parts += '<img class="visualizer__piece" src="' + src + '" style="width:' + t.offw + 'px;bottom:' + bottom + 'px;" alt="">';
      bottom += t.offo;
    }
    return '<div class="visualizer__col visualizer__col--aside-right">' + parts + '<div class="visualizer__aside-label">Offering plates</div></div>';
  }

  function buildVizCombined(c, f, interior) {
    const t = VIZ_TUNE;
    let parts = [];
    let bottom = 22;
    for (let n = 0; n < c.breadTray; n++) {
      parts.push(vizPieceHTML(vizImg('breadTray', f), t.btw, bottom));
      bottom += t.bto;
    }
    if (c.breadTray > 0) bottom += t.bridge;
    for (let n = 0; n < c.cupTray; n++) {
      parts.push(vizPieceHTML(vizImg('cupTray', f), t.ctw, bottom));
      bottom += t.cto;
    }
    if (c.cupTrayLid > 0) {
      const lidB = c.cupTray > 0 ? bottom + t.ctly : 22;
      parts.push(vizPieceHTML(vizImg('cupTrayLid', f), t.ctlw, lidB));
    }
    const hasOffering = c.offeringTray > 0;
    const hasBreadTrayLid = c.breadTrayLid > 0;
    // Shift main tower left when offerings are visible, so they don't visually crowd
    const centerClass = hasOffering ? 'visualizer__col--center-shifted' : 'visualizer__col--center';
    let html = '<div class="visualizer__main-area"><div class="visualizer__col ' + centerClass + '">' + parts.join('') + '</div>';
    if (hasBreadTrayLid) {
      const lid = '<img class="visualizer__piece" src="' + vizImg('breadTrayLid', f) + '" style="width:' + t.btlw + 'px;bottom:80px;opacity:.88;" alt="">';
      html += '<div class="visualizer__col visualizer__col--aside-left">' + lid + '<div class="visualizer__aside-label">Bread tray lid · stored separately</div></div>';
    }
    html += vizOfferingStack(c, f, interior);
    html += '</div>';
    return html;
  }

  function buildVizSeparate(c, f, interior) {
    const t = VIZ_TUNE;
    const sCt = Math.round(t.ctw * 0.72);
    const sCtl = Math.round(t.ctlw * 0.72);
    const sBt = Math.round(t.btw * 0.72);
    const sBtl = Math.round(t.btlw * 0.72);
    const sCto = Math.round(t.cto * 0.85);
    const sBto = Math.round(t.bto * 0.85);
    let cupCol = '', breadCol = '';
    let b = 22;
    for (let n = 0; n < c.cupTray; n++) { cupCol += vizPieceHTML(vizImg('cupTray', f), sCt, b); b += sCto; }
    if (c.cupTrayLid > 0) {
      const lidB = c.cupTray > 0 ? b + Math.round(t.ctly * 0.85) : 22;
      cupCol += vizPieceHTML(vizImg('cupTrayLid', f), sCtl, lidB);
    }
    b = 22;
    for (let n = 0; n < c.breadTray; n++) { breadCol += vizPieceHTML(vizImg('breadTray', f), sBt, b); b += sBto; }
    if (c.breadTrayLid > 0) {
      const lidB = c.breadTray > 0 ? b + Math.round(t.btly * 0.85) : 22;
      breadCol += vizPieceHTML(vizImg('breadTrayLid', f), sBtl, lidB);
    }
    const hasOffering = c.offeringTray > 0;
    let html = '<div class="visualizer__main-area">';
    const cupColClass = hasOffering ? 'visualizer__col--sep-left' : 'visualizer__col--left';
    const breadColClass = hasOffering ? 'visualizer__col--sep-mid' : 'visualizer__col--right';
    if (c.cupTray > 0 || c.cupTrayLid > 0) html += '<div class="visualizer__col ' + cupColClass + '">' + cupCol + '</div>';
    if (c.breadTray > 0 || c.breadTrayLid > 0) html += '<div class="visualizer__col ' + breadColClass + '">' + breadCol + '</div>';
    html += vizOfferingStack(c, f, interior);
    html += '</div>';
    return html;
  }

  function renderVisualizer() {
    if (!vizStackEl) return;
    // Re-pick the right tune for current viewport (handles resize / rotation)
    VIZ_TUNE = pickVizTune();
    const result = getVisualizerCounts();
    const c = result.counts;
    const interior = result.interior;
    const total = c.cupTray + c.cupTrayLid + c.breadTray + c.breadTrayLid + c.offeringTray;
    if (total === 0) {
      vizStackEl.innerHTML = '';
      if (vizBadgeEl) vizBadgeEl.style.display = 'none';
      return;
    }
    const combined = shouldCombineView(c);
    if (vizBadgeEl) {
      if (viewMode === 'auto') {
        vizBadgeEl.style.display = '';
        vizBadgeEl.textContent = 'Auto · ' + (combined ? 'Combined' : 'Separate');
      } else {
        vizBadgeEl.style.display = 'none';
      }
    }
    vizStackEl.innerHTML = combined ? buildVizCombined(c, currentFinish, interior) : buildVizSeparate(c, currentFinish, interior);
  }

  // Re-render the visualizer on viewport change (rotation, browser resize) so it picks up the right VIZ_TUNE
  let _vizResizeTimer = null;
  window.addEventListener('resize', () => {
    if (_vizResizeTimer) clearTimeout(_vizResizeTimer);
    _vizResizeTimer = setTimeout(() => renderVisualizer(), 150);
  }, { passive: true });

  updateUI();
}

function initProductForm(form) {
  const jsonScript = document.querySelector('[data-product-json]');
  if (!jsonScript) return;

  let product;
  try {
    product = JSON.parse(jsonScript.textContent);
  } catch (e) {
    console.error('Product JSON parse failed', e);
    return;
  }

  const select = form.querySelector('[data-variant-select]');
  const optionButtons = form.querySelectorAll('.variant-option-btn');
  const submitBtn = form.querySelector('[data-product-submit]');
  const priceEl = document.querySelector('[data-product-price]');
  const compareAtEl = document.querySelector('[data-product-compare-at-price]');
  const imageEl = document.querySelector('[data-product-image]');

  // Shopify CDN images accept a ?width= param. Swap in a display-sized version
  // instead of the full-res original (multi-MB PNG) so a color change doesn't
  // stall on a fresh download. Preserves protocol + any existing query string.
  function sizedImageUrl(src, width) {
    if (!src) return src;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (/[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  // Initial selected options from the currently-selected variant
  const initialId = select ? parseInt(select.value) : null;
  const initialVariant = product.variants.find(v => v.id === initialId) || product.variants[0];
  const selectedOptions = initialVariant ? [...initialVariant.options] : [];

  function findVariant() {
    return product.variants.find(v =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );
  }

  // Is there any in-stock variant where THIS option button's value combines with
  // the OTHER currently-selected options? Used to grey out unavailable swatches/sizes.
  function isOptionAvailable(optionIndex, optionValue) {
    return product.variants.some(v => {
      if (!v.available) return false;
      if (v.options[optionIndex] !== optionValue) return false;
      return v.options.every((opt, i) => i === optionIndex || opt === selectedOptions[i]);
    });
  }

  function updateUI() {
    const variant = findVariant();

    // Visual selected state on option buttons
    optionButtons.forEach(btn => {
      const idx = parseInt(btn.dataset.optionIndex);
      const val = btn.dataset.optionValue;
      const isSelected = selectedOptions[idx] === val;
      const isSwatch = btn.classList.contains('variant-swatch');
      // Show as unavailable when the combination of this option + the other
      // selected options doesn't exist or is sold out. Selected buttons always
      // render as selected (the submit button conveys overall unavailability).
      const isAvailable = isOptionAvailable(idx, val);
      btn.classList.toggle('is-selected', isSelected);
      btn.classList.toggle('is-unavailable', !isAvailable && !isSelected);
      // Selected: gold ring (via outline class below). Unselected: subtle but visible light border so
      // dark swatches (black, navy, etc.) don't disappear against the dark page background.
      btn.style.borderColor = isSelected ? 'var(--color-accent)' : 'rgba(245,240,232,.28)';
      // Color swatches keep their inline background hex; only text-pill buttons get the gold-tint treatment.
      if (!isSwatch) {
        btn.style.background = isSelected ? 'rgba(200,169,110,.08)' : 'transparent';
        btn.style.color = isSelected ? 'var(--color-accent)' : 'var(--color-text)';
      }
    });

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Unavailable';
      }
      return;
    }

    // Hidden select drives the form submission
    if (select) select.value = variant.id;

    // Price
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    if (compareAtEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareAtEl.textContent = formatMoney(variant.compare_at_price);
        compareAtEl.style.display = '';
      } else {
        compareAtEl.style.display = 'none';
      }
    }

    // Submit button
    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      submitBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    // Featured image swap — variant.featured_image can be a URL string OR an object
    // with .src depending on Shopify's JSON serializer. Handle both shapes, and try
    // featured_media.preview_image as a fallback (newer Shopify themes).
    if (imageEl) {
      let newSrc = null;
      if (variant.featured_image) {
        newSrc = typeof variant.featured_image === 'string'
          ? variant.featured_image
          : variant.featured_image.src;
      }
      if (!newSrc && variant.featured_media && variant.featured_media.preview_image) {
        newSrc = variant.featured_media.preview_image.src;
      }
      if (newSrc) {
        imageEl.src = sizedImageUrl(newSrc, 1000);
      }
    }
  }

  // Wire option-button clicks + hover behavior
  optionButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.optionIndex);
      const val = btn.dataset.optionValue;
      selectedOptions[idx] = val;
      updateUI();
    });
    btn.addEventListener('mouseenter', () => {
      if (!btn.classList.contains('is-selected')) {
        btn.style.borderColor = 'var(--color-accent)';
      }
    });
    btn.addEventListener('mouseleave', () => {
      if (!btn.classList.contains('is-selected')) {
        // Match the visible default set in updateUI (and inline style on swatches) so
        // dark swatches don't fade out after a hover.
        btn.style.borderColor = 'rgba(245,240,232,.28)';
      }
    });
  });

  updateUI();

  // Warm the browser cache for every color's image (at display size) so the
  // FIRST switch to a color is instant instead of waiting on a fresh download.
  function preloadVariantImages() {
    const seen = new Set();
    product.variants.forEach(v => {
      let src = null;
      if (v.featured_image) {
        src = typeof v.featured_image === 'string' ? v.featured_image : v.featured_image.src;
      }
      if (!src && v.featured_media && v.featured_media.preview_image) {
        src = v.featured_media.preview_image.src;
      }
      if (!src) return;
      const url = sizedImageUrl(src, 1000);
      if (seen.has(url)) return;
      seen.add(url);
      const img = new Image();
      img.src = url;
    });
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(preloadVariantImages);
  } else {
    setTimeout(preloadVariantImages, 400);
  }
}

function updateCartProgress(totalCents) {
  const bar = document.querySelector('[data-cart-progress]');
  if (!bar) return;
  const fill = bar.querySelector('[data-cart-progress-fill]');
  const text = bar.querySelector('[data-cart-progress-text]');
  const threshold = parseInt(bar.dataset.threshold, 10) || 7500;
  if (totalCents >= threshold) {
    if (fill) fill.style.width = '100%';
    bar.classList.add('is-complete');
    if (text) text.textContent = '✓ You qualify for free shipping';
  } else {
    const pct = Math.max(0, Math.min(100, (totalCents / threshold) * 100));
    if (fill) fill.style.width = pct + '%';
    bar.classList.remove('is-complete');
    const remaining = (threshold - totalCents) / 100;
    if (text) text.textContent = 'Add $' + remaining.toFixed(2) + ' more for free shipping';
  }
}

/* Apparel volume discount (2 shirts = 10%, 3+ = 15%). Recounts apparel quantity from
   the live cart lines and updates the message, tier bar, and savings row. The applied
   savings amount comes from the cart's total_discount (server-rendered on load, from the
   cart/change.js response thereafter). */
function updateApparelDiscount(totalDiscountCents) {
  const box = document.querySelector('[data-apparel-discount]');
  if (!box) return;

  let qty = 0;
  document.querySelectorAll('[data-cart-line][data-apparel="true"]').forEach(line => {
    const input = line.querySelector('[data-qty-input]');
    qty += input ? (parseInt(input.value, 10) || 0) : 0;
  });

  box.style.display = qty < 1 ? 'none' : '';
  const fill = box.querySelector('[data-apparel-discount-fill]');
  if (fill) fill.style.width = Math.min(100, Math.round((qty / 3) * 100)) + '%';
  box.classList.toggle('is-complete', qty >= 3);

  const saved = (typeof totalDiscountCents === 'number' && totalDiscountCents > 0)
    ? formatMoney(totalDiscountCents) : null;

  const text = box.querySelector('[data-apparel-discount-text]');
  if (text) {
    if (qty >= 3) {
      text.textContent = "🎉 You're saving 15% on apparel" + (saved ? ' — you saved ' + saved : '') + '!';
    } else if (qty === 2) {
      text.textContent = '🎉 10% apparel discount applied' + (saved ? ' (' + saved + ' off)' : '') + ' — add 1 more shirt to save 15%';
    } else if (qty === 1) {
      text.textContent = 'Add 1 more apparel item to save 10% — or 2 more for 15%';
    }
  }

  const savingsRow = document.querySelector('[data-cart-savings-row]');
  const savingsEl = document.querySelector('[data-cart-savings]');
  if (savingsRow) {
    savingsRow.style.display = saved ? 'flex' : 'none';
    if (saved && savingsEl) savingsEl.textContent = '− ' + saved;
  }
}

function initCartContent(form) {
  const lines = form.querySelectorAll('[data-cart-line]');
  const subtotalEl = form.querySelector('[data-cart-subtotal]');

  // Initialize the free-shipping progress bar from the server-rendered cart total
  const progressEl = document.querySelector('[data-cart-progress]');
  if (progressEl) {
    const initialCents = parseInt(progressEl.dataset.cartTotalCents, 10) || 0;
    updateCartProgress(initialCents);
  }

  // Initialize the apparel volume-discount message from the server-rendered discount total
  const apparelBox = document.querySelector('[data-apparel-discount]');
  if (apparelBox) {
    updateApparelDiscount(parseInt(apparelBox.dataset.cartDiscountCents, 10) || 0);
  }

  function updateLine(lineEl, newQty) {
    const lineIndex = parseInt(lineEl.dataset.cartLine);
    const input = lineEl.querySelector('[data-qty-input]');
    const decBtn = lineEl.querySelector('[data-qty-decrement]');
    const incBtn = lineEl.querySelector('[data-qty-increment]');
    const linePriceEl = lineEl.querySelector('[data-line-price]');

    newQty = Math.max(0, newQty);

    // Optimistic UI: disable controls while the request is in flight
    if (input) input.disabled = true;
    if (decBtn) decBtn.disabled = true;
    if (incBtn) incBtn.disabled = true;

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: lineIndex, quantity: newQty })
    })
      .then(r => {
        if (!r.ok) throw new Error('Cart update failed');
        return r.json();
      })
      .then(cart => {
        if (newQty === 0) {
          // Line removed; cart re-indexes. Easiest correct path is a reload.
          window.location.reload();
          return;
        }
        // Update this line's price (Shopify line index is 1-based; items array is 0-based).
        // Show the discounted (final) price, striking the original when a discount applies.
        const updatedItem = cart.items[lineIndex - 1];
        if (updatedItem && linePriceEl) {
          linePriceEl.textContent = formatMoney(updatedItem.final_line_price);
          const origEl = lineEl.querySelector('[data-line-original]');
          if (origEl) {
            if (updatedItem.original_line_price > updatedItem.final_line_price) {
              origEl.textContent = formatMoney(updatedItem.original_line_price);
              origEl.style.display = 'block';
            } else {
              origEl.style.display = 'none';
            }
          }
        }
        if (subtotalEl) {
          subtotalEl.textContent = formatMoney(cart.total_price);
        }
        updateCartProgress(cart.total_price);
        if (input) input.value = newQty;
        updateApparelDiscount(cart.total_discount);
      })
      .catch(err => {
        console.error('Cart update error:', err);
      })
      .finally(() => {
        if (input) input.disabled = false;
        if (decBtn) decBtn.disabled = false;
        if (incBtn) incBtn.disabled = false;
      });
  }

  lines.forEach(line => {
    const input = line.querySelector('[data-qty-input]');
    const decBtn = line.querySelector('[data-qty-decrement]');
    const incBtn = line.querySelector('[data-qty-increment]');

    if (decBtn && input) {
      decBtn.addEventListener('click', () => updateLine(line, parseInt(input.value) - 1));
    }
    if (incBtn && input) {
      incBtn.addEventListener('click', () => updateLine(line, parseInt(input.value) + 1));
    }
    if (input) {
      input.addEventListener('change', () => updateLine(line, parseInt(input.value)));
    }
  });
}
